import { z } from "zod";

import { ERROR_TYPE } from "@/utils";
import { TokenPurpose } from "@/enum";
import { PASSWORD_REGEX } from "@/constants";
import { UserLanguage } from "@/enum/user.enum";

import { FetchMeResponseSchema } from "../../Login/schema/login";

const { PASSWORD_GUIDELINES, PASSWORD_MISMATCH, REQUIRED, ACCEPT_TERMS } = ERROR_TYPE;

export const SetPasswordSchema = z
	.object({
		termsAndConditions: z.boolean(),
		password: z
			.string()
			.min(1, { message: REQUIRED })
			.regex(PASSWORD_REGEX, PASSWORD_GUIDELINES)
			.trim(),
		confirmPassword: z.string().min(1, { message: REQUIRED }).trim(),
	})
	.superRefine(({ confirmPassword, password, termsAndConditions }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				code: "custom",
				path: ["confirmPassword"],
				message: PASSWORD_MISMATCH,
			});
		}

		if (!termsAndConditions) {
			ctx.addIssue({
				code: "custom",
				path: ["termsAndConditions"],
				message: ACCEPT_TERMS,
			});
		}
	});

export const SetPasswordRequestSchema = z.object({
	password: z.string().min(8),
	confirmPassword: z.string().min(8),
	token: z.string().min(1, { message: REQUIRED }),
	purpose: z.nativeEnum(TokenPurpose),
});

export const AcceptInvitationSchema = z
	.object({
		termsAndConditions: z.boolean(),
		firstName: z.string().min(1, { message: REQUIRED }).trim(),
		lastName: z.string().min(1, { message: REQUIRED }).trim(),
		password: z
			.string()
			.min(1, { message: REQUIRED })
			.regex(PASSWORD_REGEX, PASSWORD_GUIDELINES)
			.trim(),
		confirmPassword: z.string().min(1, { message: REQUIRED }).trim(),
		userLanguage: z.nativeEnum(UserLanguage).optional(),
	})
	.superRefine(({ confirmPassword, password, termsAndConditions }, ctx) => {
		if (confirmPassword !== password) {
			ctx.addIssue({
				code: "custom",
				path: ["confirmPassword"],
				message: PASSWORD_MISMATCH,
			});
		}

		if (!termsAndConditions) {
			ctx.addIssue({
				code: "custom",
				path: ["termsAndConditions"],
				message: ACCEPT_TERMS,
			});
		}
	});

export const AcceptInvitationRequestSchema = z.object({
	firstName: z.string().min(1, { message: REQUIRED }).trim(),
	lastName: z.string().min(1, { message: REQUIRED }).trim(),
	password: z.string().min(8),
	userLanguage: z.nativeEnum(UserLanguage).optional(),
	confirmPassword: z.string().min(8),
	token: z.string().min(1, { message: REQUIRED }),
	purpose: z.nativeEnum(TokenPurpose),
});

export const AcceptInvitationResponseSchema = z.object({
	status: z.string().optional(),
	user: FetchMeResponseSchema.optional(),
	accessToken: z.string().optional(),
	otpToken: z.string().optional(),
	qrCode: z.string().optional(),
});

export type SetPasswordType = z.infer<typeof SetPasswordSchema>;
export type SetPasswordBody = z.infer<typeof SetPasswordRequestSchema>;
export type AcceptInvitationType = z.infer<typeof AcceptInvitationSchema>;
export type AcceptInvitationBody = z.infer<typeof AcceptInvitationRequestSchema>;
